import React, { Fragment, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Card from 'reactstrap/lib/Card';
import Input from 'reactstrap/lib/Input';
import Button from 'reactstrap/lib/Button';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingConstant } from 'client/tracking/constant';
import { STEP, ANSWER, SURVEY_BUTTONS } from 'site-modules/shared/constants/smiley-survey';

import './smiley-survey.scss';

function SmileySurveyUI({
  className = null,
  cardClassName = 'w-100 px-0_25',
  questionClassName = null,
  question,
  creativeId,
  smileyTrackingId,
  textTrackingIdMap,
}) {
  const [currentStep, setCurrentStep] = useState(STEP.SURVEY);
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [isTextEntered, setTextEntered] = useState(false);
  const textRef = useRef();

  const onSurveySubmit = useCallback(
    ({
      currentTarget: {
        dataset: { answer },
      },
    }) => {
      setCurrentAnswer(answer);
      setCurrentStep(STEP.TEXT);
    },
    []
  );

  const onTextChange = useCallback(({ target: { value } }) => setTextEntered(!!value), []);

  const onTextSubmit = useCallback(() => {
    const value = get(textRef, 'current.value');

    TrackingHandler.fireEvent(textTrackingIdMap[currentAnswer], {
      event_data: {
        value,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        creative_id: creativeId,
      },
    });

    setCurrentStep(STEP.THANK_YOU);
  }, [textRef, currentAnswer, textTrackingIdMap, creativeId]);

  return (
    <div
      className={classnames('smiley-survey d-flex justify-content-center', className)}
      data-tracking-parent={creativeId}
    >
      <Card className={classnames('survey-card align-items-center pt-1_5 pb-2', cardClassName)}>
        {currentStep === STEP.SURVEY && (
          <Fragment>
            <h2 className={classnames('size-20 text-gray-darker text-center fw-medium mb-1_5', questionClassName)}>
              {question}
            </h2>
            <ul className="list-unstyled mb-0 d-flex">
              {SURVEY_BUTTONS.map(({ icon, label }) => (
                <li key={icon} className="mx-0_75">
                  <button
                    onClick={onSurveySubmit}
                    className="survey-btn d-flex p-0_75 text-primary-darker text-decoration-none border-0 bg-white"
                    style={{ height: '72px', width: '72px' }}
                    data-tracking-id={smileyTrackingId}
                    data-tracking-value={label}
                    data-answer={label}
                  >
                    <span className="visually-hidden">{label}</span>
                    <i className={classnames('survey-icon', icon)} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          </Fragment>
        )}
        {currentStep === STEP.TEXT && (
          <div className="w-100 text-center px-1_5">
            <h2 id="text-label" className="size-20 text-gray-darker fw-medium mb-1_5">
              Tell us more about why you
              <br />
              chose your rating
            </h2>
            <Input
              aria-labelledby="text-label"
              innerRef={textRef}
              className="text-area-input mb-2"
              name="smiley-survey-text"
              type="textarea"
              onChange={onTextChange}
              maxLength={300}
              rows={5}
            />
            <Button
              onClick={onTextSubmit}
              color="primary-b"
              className="submit-btn text-transform-none px-3 mx-auto"
              disabled={!isTextEntered}
            >
              Submit<span className="visually-hidden"> your feedback</span>
            </Button>
          </div>
        )}
        {currentStep === STEP.THANK_YOU && (
          <Fragment>
            <h2 className="size-24 fw-medium text-gray-darker mb-1_5">Thank you</h2>
            <div className="size-20 text-gray-darker">We value your feedback.</div>
          </Fragment>
        )}
      </Card>
    </div>
  );
}

SmileySurveyUI.propTypes = {
  question: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  creativeId: PropTypes.string.isRequired,
  smileyTrackingId: PropTypes.string.isRequired,
  textTrackingIdMap: PropTypes.shape({
    [ANSWER.DISSATISFIED]: PropTypes.string,
    [ANSWER.NEUTRAL]: PropTypes.string,
    [ANSWER.SATISFIED]: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  cardClassName: PropTypes.string,
  questionClassName: PropTypes.string,
};

export const SmileySurvey = React.memo(SmileySurveyUI);
